import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { API_URL, apiRequest } from '../lib/api';

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [offset, setOffset] = useState(0);
  const [audit, setAudit] = useState(null);
  const [dateState, setDateState] = useState(null);
  const [dateResult, setDateResult] = useState(null);
  const [confirmDates, setConfirmDates] = useState(false);
  const [yearState, setYearState] = useState(null);
  const [yearResult, setYearResult] = useState(null);
  const [reviews, setReviews] = useState(null);
  const [reviewNote, setReviewNote] = useState({});
  const headers = () => ({ Authorization: `Bearer ${sessionStorage.getItem('token')}`, 'Content-Type': 'application/json' });
  const load = async () => {
    setBusy(true); setError(''); setData(null);
    try { setData(await apiRequest(`/api/admin/overview?offset=${offset}`, { headers: headers() })); }
    catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  // Each page change starts a fresh bounded inbox request.
  useEffect(() => { load(); }, [offset]);
  const update = async (path, body) => {
    setBusy(true); setError('');
    try { await apiRequest(path, { method: 'POST', headers: headers(), body: JSON.stringify(body) }); await load(); }
    catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  const runAudit = async () => {
    setBusy(true); setError(''); setAudit(null);
    try {
      const response = await fetch(`${API_URL}/api/health/invariants`, { headers: headers(), signal: AbortSignal.timeout(20000) });
      const report = await response.json();
      if (![200, 503].includes(response.status) || !Array.isArray(report.findings)) throw new Error('Unable to run operational checks. Try again.');
      setAudit(report);
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  // The date migration is irreversible, so it is read first, confirmed
  // separately, and never a single click from the page loading.
  const loadDateState = async () => {
    setBusy(true); setError(''); setDateResult(null); setConfirmDates(false);
    try { setDateState(await apiRequest('/api/admin/maintenance/date-truncation', { headers: headers() })); }
    catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  const runDateTruncation = async () => {
    setBusy(true); setError('');
    try {
      setDateResult(await apiRequest('/api/admin/maintenance/date-truncation', {
        method: 'POST', headers: headers(),
        body: JSON.stringify({ confirm: 'truncate-dates-permanently' }),
      }));
      setConfirmDates(false);
      setDateState(await apiRequest('/api/admin/maintenance/date-truncation', { headers: headers() }));
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  // Filling a gap from a value already in the same row. No confirmation
  // step, unlike the truncation above: nothing is destroyed and nothing is
  // overwritten, so the dangerous thing here would be leaving it undone.
  const loadYearState = async () => {
    setBusy(true); setError(''); setYearResult(null);
    try { setYearState(await apiRequest('/api/admin/maintenance/missing-years', { headers: headers() })); }
    catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  const runYearRepair = async () => {
    setBusy(true); setError('');
    try {
      setYearResult(await apiRequest('/api/admin/maintenance/missing-years', {
        method: 'POST', headers: headers(),
      }));
      setYearState(await apiRequest('/api/admin/maintenance/missing-years', { headers: headers() }));
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  // Held extracts. A researcher is waiting on each of these, so they load
  // with the page rather than behind a button: a review nobody looks at is a
  // refusal with extra steps.
  const loadReviews = async () => {
    try { setReviews(await apiRequest('/api/admin/release-reviews', { headers: headers() })); }
    catch (e) { setError(e.message); }
  };
  useEffect(() => { loadReviews(); }, []);
  const decideReview = async (id, decision) => {
    setBusy(true); setError('');
    try {
      await apiRequest(`/api/admin/release-reviews/${id}`, {
        method: 'POST', headers: headers(),
        body: JSON.stringify({ decision, note: reviewNote[id] || '' }),
      });
      await loadReviews();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  const button = 'border border-white/40 rounded px-4 py-2 disabled:opacity-40';
  return <main className="max-w-6xl mx-auto px-6 py-16 text-white">
    <h1 className="text-4xl mb-4">Admin workspace</h1>
    <Link className="inline-block text-emerald-300 underline mb-4" to="/projects">Plan research and discover projects</Link>
    <p className="text-white/70 mb-6">Review incoming requests, confirm and approve researchers for testing, and check platform readiness.</p>
    <div className="flex gap-4 mb-6"><button className={button} disabled={busy} onClick={load}>Refresh inbox</button><Link className={button} to="/research-readiness">Review study evidence</Link></div>
    {error && <p role="alert" className="text-red-300 mb-6">{error}</p>}
    {busy && <p role="status">Working…</p>}
    {data && <>
      <h2 className="text-2xl mt-8 mb-3">Incoming requests</h2>
      <p className="text-white/70 mb-4">Requests appear here. Status changes do not send email or create accounts. Research access is approved separately below.</p>
      {!data.contacts.length && <p>No requests on this page.</p>}
      {data.contacts.map(c => <section className="border border-white/20 rounded p-5 mb-3 break-words" key={c.id}>
        <h3 className="text-xl">{c.name} — {c.email}</h3><p>{c.organization || 'No organization supplied'} · {c.interest || 'General request'}</p>
        <p className="whitespace-pre-wrap my-3">{c.message}</p>
        <label>Request status <select className="bg-black border p-2" disabled={busy} value={c.status} onChange={e => update(`/api/admin/contacts/${c.id}/status`, { status: e.target.value })}>
          <option value="new">New</option><option value="contacted">Contacted</option><option value="closed">Closed</option>
        </select></label>
      </section>)}
      <h2 className="text-2xl mt-8 mb-3">Researcher access</h2>
      <p className="text-white/70 mb-4">Two decisions, kept separate on purpose. First confirm who the applicant is and where they work. Only then approve research access. Approval does not authorize live patient data, licensing, or EHR access.</p>
      {!data.researchers.length && <p>No researcher accounts on this page.</p>}
      {data.researchers.map(u => <section className="border border-white/20 rounded p-5 mb-3" key={u.id}>
        <h3 className="text-xl break-words">{u.name} — {u.email}</h3><p>{u.organization || 'No organization supplied'}</p>
        <p className="my-3">{u.approved ? 'Approved' : 'Pending approval'} · {u.verified ? 'Identity confirmed' : 'Identity not confirmed'} · {u.active ? 'Active account' : 'Inactive account'}</p>
        {!u.verified && <p className="text-white/50 text-sm mb-3">Establish who this person is and where they work before confirming — a call to the department, an institutional directory, a colleague who knows them. There is no email link; controlling a mailbox does not establish affiliation.</p>}
        <div className="flex flex-wrap gap-3">
          <button className={button} disabled={busy || (u.verified && u.approved)} onClick={() => update(`/api/admin/researchers/${u.id}/decision`, { decision: u.verified ? 'withdraw-identity' : 'confirm-identity' })}>{u.verified ? 'Withdraw identity confirmation' : 'Confirm identity and affiliation'}</button>
          <button className={button} disabled={busy || (!u.approved && (!u.verified || !u.active))} onClick={() => update(`/api/admin/researchers/${u.id}/decision`, { decision: u.approved ? 'revoke' : 'approve' })}>{u.approved ? 'Revoke research access' : 'Approve for testing'}</button>
        </div>
      </section>)}
      <div className="flex gap-4 my-6"><button className={button} disabled={busy || offset === 0} onClick={() => setOffset(offset - 50)}>Previous</button><span>Page {offset / 50 + 1}</span><button className={button} disabled={busy || !data.has_more} onClick={() => setOffset(offset + 50)}>Next</button></div>
      <h2 className="text-2xl mt-8 mb-3">Availability and setup</h2>
      <p className="text-white/70 mb-4">These are server configuration states, not proof of successful end-to-end operation. Live integrations and commercial licensing still require institutional setup.</p>
      <ul className="space-y-2">{data.capabilities.map(c => <li key={c.name}>{c.name}: <strong>{c.enabled ? 'Enabled in configuration' : 'Unavailable / not enabled'}</strong></li>)}</ul>
    </>}
    <h2 className="text-2xl mt-8 mb-3">Operational checks</h2>
    <p className="text-white/70 mb-4">Run read-only database and privacy safeguards checks. These checks do not certify regulatory compliance.</p>
    <button className={button} disabled={busy} onClick={runAudit}>Run checks</button>
    {audit && <section className="mt-4"><p>{audit.summary}</p><ul className="space-y-3 mt-3">{audit.findings.map(f => <li key={f.name}>{f.passed ? 'Pass' : 'Needs attention'} — {f.summary} ({f.severity})</li>)}</ul></section>}

    <h2 className="text-2xl mt-10 mb-3">Extracts held for disclosure review</h2>
    <p className="text-white/70 mb-4">
      Two extracts whose subject sets differ by only a few people identify those
      people to anyone holding both files, and each file on its own passes every
      automatic check. These are held before the file is built. The requester is
      told it is under review and nothing else — what it collided with is exactly
      what they must not have.
    </p>

    {reviews && reviews.length === 0 && (
      <p className="text-white/50">Nothing is held. No researcher is waiting.</p>
    )}
    {reviews && reviews.map((review) => (
      <section key={review.id} className="mb-4 border border-amber-500/30 rounded p-5">
        <p className="mb-1">
          <strong>{review.study_name || 'Untitled study'}</strong>
          {review.requested_by ? ` · requested by ${review.requested_by}` : ''}
          {review.requested_by_organization ? ` (${review.requested_by_organization})` : ''}
        </p>
        <p className="text-white/70 mb-1">
          {review.subjects_differing} subject(s) differ from an earlier release —
          below the floor of {review.threshold}. Releasing both would identify them.
        </p>
        <p className="text-white/50 mb-1">
          Shape: {review.shape}
          {review.detail?.prior_release_downloaded
            ? ' · the earlier extract has been downloaded'
            : ' · the earlier extract has not been downloaded yet'}
        </p>
        <p className="text-white/40 text-sm mb-4">
          Approving re-runs the extract from scratch, so consent, approvals and
          disclosure risk are all re-checked. If the people in it have changed
          since it was held, it returns here rather than going out on a
          decision made about a different group.
        </p>
        <label className="block mb-3">
          <span className="text-white/60 text-sm">Why (recorded either way)</span>
          <textarea
            className="w-full bg-transparent border border-white/20 rounded p-2 mt-1"
            rows={2}
            value={reviewNote[review.id] || ''}
            onChange={(e) => setReviewNote({ ...reviewNote, [review.id]: e.target.value })}
          />
        </label>
        <div className="flex flex-wrap gap-3">
          <button className={button} disabled={busy}
                  onClick={() => decideReview(review.id, 'approve')}>
            Release it
          </button>
          <button className={button} disabled={busy}
                  onClick={() => decideReview(review.id, 'decline')}>
            Do not release
          </button>
        </div>
      </section>
    ))}

    <h2 className="text-2xl mt-10 mb-3">Stored date precision</h2>
    <p className="text-white/70 mb-4">
      Safe Harbor permits no date element more precise than a year. New records already store a
      year only. Records created before that change may still hold a month and a day; this
      removes them permanently.
    </p>
    <button className={button} disabled={busy} onClick={loadDateState}>Check stored dates</button>

    {dateState && <section className="mt-4 border border-white/20 rounded p-5">
      {dateState.already_applied ? (
        <p>Already applied. No stored clinical date carries a month or a day.</p>
      ) : (<>
        <p className="mb-2">
          <strong>{dateState.rows_with_month_and_day}</strong> record(s) still hold a month and a day.
        </p>
        <p className="text-amber-300/80 mb-4">
          This cannot be undone. The month and day are destroyed, not archived. Restoring them
          would need a backup taken beforehand.
        </p>
        {!confirmDates ? (
          <button className={button} disabled={busy} onClick={() => setConfirmDates(true)}>
            Truncate these dates to the year
          </button>
        ) : (
          <div className="flex flex-wrap gap-3 items-center">
            <span>Permanently destroy month and day on {dateState.rows_with_month_and_day} record(s)?</span>
            <button className={button} disabled={busy} onClick={runDateTruncation}>Yes, truncate permanently</button>
            <button className={button} disabled={busy} onClick={() => setConfirmDates(false)}>Cancel</button>
          </div>
        )}
      </>)}
    </section>}
    {dateResult && <p role="status" className="mt-3">
      {dateResult.applied
        ? `Truncated ${dateResult.rows_truncated} record(s). ${dateResult.rows_remaining} remain.`
        : dateResult.reason}
    </p>}

    <h2 className="text-2xl mt-10 mb-3">Records missing their year</h2>
    <p className="text-white/70 mb-4">
      A record with no year matches no cohort with a date range on it, so a patient who
      contributed can be invisible to the studies most likely to want them. Where the year is
      still present in the record's own de-identified payload, this copies it into the column
      researchers search. Records that genuinely never carried a date are left alone, and a
      year already stored is never overwritten.
    </p>
    <button className={button} disabled={busy} onClick={loadYearState}>Check stored years</button>

    {yearState && <section className="mt-4 border border-white/20 rounded p-5">
      {!yearState.rows_repairable ? (
        <p>Nothing to repair. Every record that states a year has it stored.</p>
      ) : (<>
        <p className="mb-2">
          <strong>{yearState.rows_repairable}</strong> record(s) carry a year that was not stored.
        </p>
        <p className="text-white/60 mb-4">
          {yearState.rows_with_no_year_recorded_anywhere} further record(s) state no year at all
          and will stay as they are.
        </p>
        <button className={button} disabled={busy} onClick={runYearRepair}>
          Restore these years
        </button>
      </>)}
    </section>}
    {yearResult && <p role="status" className="mt-3">
      {yearResult.applied
        ? `Restored the year on ${yearResult.rows_repaired} record(s).`
        : yearResult.reason}
    </p>}
  </main>;
}
